import SalesService from "../../../services/sales.service";
import Utils from "../../../utils/Utils";
import { obtenerPreparadoresNave1 } from "./preparadorNave1";

/** Pedidos de un grupo del listado, con número y tipo */
export const pedidosDeGrupo = (grupo) =>
    (grupo.pedidos || []).map((pedido) => ({
        numero: pedido.numero || pedido,
        tipo: pedido.tipo || 'albaran'
    }));

/**
 * Productos de todos los pedidos de un grupo de Nave 2, agrupados por referencia
 * (unidades sumadas), con la forma que imprime PrintOrder: igual que en el punteo.
 *
 * DECISION: a diferencia de OrderN2Grupo, si un pedido no se puede cargar falla todo:
 * una etiqueta a la que le faltan productos es peor que no imprimirla.
 *
 * @param {Object} grupo - Grupo del listado (pedidos [{numero, tipo}])
 * @param {number} year - Ejercicio
 * @returns {Promise<Array<{ref, ean, name, units, location, stock}>>}
 */
export const cargarProductosGrupo = async (grupo, year) => {
    // El nombre de la relación de EAN depende del ejercicio, igual que en OrderN2
    const barrasModels = year !== Utils.getCurrentYear() ? `BarrasModel${year}s` : 'BarrasModels';

    const lineasPorPedido = await Promise.all(
        pedidosDeGrupo(grupo).map(({ numero, tipo }) => SalesService.getSalesProductN2(numero, year, tipo))
    );

    const productos = new Map();
    lineasPorPedido.flat().forEach((linea) => {
        const ref = linea.ARTICULO;
        if (!ref) return;

        if (!productos.has(ref)) {
            const info = linea.INFO_ARTICULO || {};
            productos.set(ref, {
                ref,
                ean: info[barrasModels] || [],
                name: info.NOMBRE || '',
                units: 0,
                location: info.UBICACION || '',
                stock: info.Stocks2Models || []
            });
        }
        productos.get(ref).units += linea.UNIDADES || 0;
    });

    return Array.from(productos.values());
};

/**
 * Datos de la etiqueta de preparación de un grupo de Nave 2: sus productos y la
 * cabecera con el preparador del grupo y el de nave 1 de cada pedido.
 *
 * @param {Object} grupo - Grupo del listado (id, preparador, pedidos [{numero, tipo}])
 * @param {number} year - Ejercicio
 * @param {Array} [productosPrecargados] - Productos ya cargados (useProductosGruposN2);
 *   si llegan, solo se consultan los preparadores de nave 1
 * @returns {Promise<{order: Object, products: Array}>} Lo que PrintOrder recibe por props
 */
export const cargarTicketGrupo = async (grupo, year, productosPrecargados) => {
    const [products, preparadoresN1] = await Promise.all([
        productosPrecargados ?? cargarProductosGrupo(grupo, year),
        obtenerPreparadoresNave1(pedidosDeGrupo(grupo), year)
    ]);

    return {
        order: {
            num: grupo.id,
            tipo: 'grupo',
            route: 'Grupo',
            preparer: grupo.preparador || 'Sin asignar',
            preparadoresN1
        },
        products
    };
};

/**
 * Datos de la etiqueta de preparación de un pedido suelto de Nave 2, la misma que
 * imprime el "Imprimir" del punteo del pedido.
 *
 * @param {Object} pedido - Fila del listado (num, type, route, preparer)
 * @param {number} year - Ejercicio
 * @param {Array} [productosPrecargados] - Productos ya cargados (useProductosPedidosN2)
 * @returns {Promise<{order: Object, products: Array}>} Lo que PrintOrder recibe por props
 */
export const cargarTicketPedido = async (pedido, year, productosPrecargados) => {
    // Un pedido es un grupo de uno: se reutiliza la carga de productos del grupo
    const comoGrupo = { pedidos: [{ numero: pedido.num, tipo: pedido.type }] };
    const [products, preparadoresN1] = await Promise.all([
        productosPrecargados ?? cargarProductosGrupo(comoGrupo, year),
        obtenerPreparadoresNave1(pedidosDeGrupo(comoGrupo), year)
    ]);

    return {
        order: {
            num: pedido.num,
            tipo: pedido.type,
            route: pedido.route,
            preparer: pedido.preparer ?? '0',
            preparadoresN1
        },
        products
    };
};
